/**
 * NetworkWorld の生成・複製・書き換え。
 *
 * Effect から呼ばれる「世界を変える」操作はすべてここに集約する。
 * 変更が起きたら true を返し、呼び出し側（シナリオエンジン）が再評価をかける。
 */

import type { DeviceId, FirewallRule, IpConfig, LinkId, NetworkWorld } from '../types/network';
import { isValidChannel, recommendChannel, type WirelessBand } from './wireless';

/** JSON から読んだ世界を安全に複製する（章のやり直し用） */
export function cloneWorld(world: NetworkWorld): NetworkWorld {
  return structuredClone(world);
}

/**
 * JSON から読み込んだデータが NetworkWorld として最低限成立しているかを検証する。
 * 問題があればメッセージの配列を返す（空なら OK）。
 * データ担当が書いた JSON の取り違えを早期に見つけるための保険。
 */
export function validateWorld(world: NetworkWorld): string[] {
  const errors: string[] = [];
  if (!world || typeof world !== 'object') return ['world が object ではありません'];
  if (!world.devices) errors.push('world.devices がありません');
  if (!world.links) errors.push('world.links がありません');
  if (!world.rooms) errors.push('world.rooms がありません');
  if (errors.length > 0) return errors;

  for (const id of Object.keys(world.devices)) {
    const device = world.devices[id];
    if (device.id !== id) errors.push(`device "${id}" の id が キーと一致しません (${device.id})`);
    if (!world.rooms[device.roomId]) {
      errors.push(`device "${id}" の roomId "${device.roomId}" が rooms にありません`);
    }
    if (!Array.isArray(device.ports)) errors.push(`device "${id}" に ports がありません`);
  }

  for (const id of Object.keys(world.links)) {
    const link = world.links[id];
    if (link.id !== id) errors.push(`link "${id}" の id がキーと一致しません (${link.id})`);
    for (const end of ['a', 'b'] as const) {
      const ref = link[end];
      const device = world.devices[ref.deviceId];
      if (!device) {
        errors.push(`link "${id}" の ${end} 側の機器 "${ref.deviceId}" が存在しません`);
        continue;
      }
      if (!device.ports.some((p) => p.index === ref.portIndex)) {
        errors.push(`link "${id}" の ${end} 側ポート ${ref.deviceId}:${ref.portIndex} が存在しません`);
      }
    }
  }
  return errors;
}

/**
 * link と port.linkId の対応を整える。
 * データ側で port.linkId を書き忘れていても、link の定義から自動で補完する。
 */
export function normalizeWorld(world: NetworkWorld): NetworkWorld {
  for (const linkId of Object.keys(world.links)) {
    const link = world.links[linkId];
    for (const end of ['a', 'b'] as const) {
      const ref = link[end];
      const device = world.devices[ref.deviceId];
      if (!device) continue;
      const port = device.ports.find((p) => p.index === ref.portIndex);
      if (port && port.linkId === null) port.linkId = linkId;
    }
  }
  return world;
}

/** ケーブルの抜き差し */
export function setLinkConnected(
  world: NetworkWorld,
  linkId: LinkId,
  end: 'a' | 'b',
  connected: boolean,
): boolean {
  const link = world.links[linkId];
  if (!link) return false;
  const key = end === 'a' ? 'connectedA' : 'connectedB';
  if (link[key] === connected) return false;
  link[key] = connected;
  return true;
}

/** ケーブルの両端をまとめて抜き差しする（UI の「挿し直す」用） */
export function setLinkFullyConnected(
  world: NetworkWorld,
  linkId: LinkId,
  connected: boolean,
): boolean {
  const a = setLinkConnected(world, linkId, 'a', connected);
  const b = setLinkConnected(world, linkId, 'b', connected);
  return a || b;
}

/** ポートの有効／無効 */
export function setPortAdminUp(
  world: NetworkWorld,
  deviceId: DeviceId,
  portIndex: number,
  adminUp: boolean,
): boolean {
  const device = world.devices[deviceId];
  if (!device) return false;
  const port = device.ports.find((p) => p.index === portIndex);
  if (!port || port.adminUp === adminUp) return false;
  port.adminUp = adminUp;
  return true;
}

/** 電源 */
export function setPowered(world: NetworkWorld, deviceId: DeviceId, powered: boolean): boolean {
  const device = world.devices[deviceId];
  if (!device || device.powered === powered) return false;
  device.powered = powered;
  return true;
}

/** 隔離 */
export function setQuarantined(
  world: NetworkWorld,
  deviceId: DeviceId,
  quarantined: boolean,
): boolean {
  const device = world.devices[deviceId];
  if (!device || !!device.quarantined === quarantined) return false;
  device.quarantined = quarantined;
  return true;
}

const IP_CONFIG_KEYS: (keyof IpConfig)[] = [
  'address',
  'subnetMask',
  'gateway',
  'dnsServers',
  'dhcp',
];

/**
 * IP 設定の部分更新。
 * Effect の patch は Record<string, unknown> なので、既知のキーだけを型安全に取り込む。
 */
export function patchIpConfig(
  world: NetworkWorld,
  deviceId: DeviceId,
  patch: Record<string, unknown>,
): boolean {
  const device = world.devices[deviceId];
  if (!device) return false;

  let changed = false;
  for (const key of IP_CONFIG_KEYS) {
    if (!(key in patch)) continue;
    const value = patch[key];
    switch (key) {
      case 'address':
      case 'gateway':
        if (value === null || typeof value === 'string') {
          if (device.ip[key] !== value) {
            device.ip[key] = value;
            changed = true;
          }
        }
        break;
      case 'subnetMask':
        if (typeof value === 'string' && device.ip.subnetMask !== value) {
          device.ip.subnetMask = value;
          changed = true;
        }
        break;
      case 'dhcp':
        if (typeof value === 'boolean' && device.ip.dhcp !== value) {
          device.ip.dhcp = value;
          changed = true;
        }
        break;
      case 'dnsServers':
        if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
          device.ip.dnsServers = value as string[];
          changed = true;
        }
        break;
    }
  }
  return changed;
}

/** 部屋の解錠（lockedUntilFlag を外す） */
export function unlockRoom(world: NetworkWorld, roomId: string): boolean {
  const room = world.rooms[roomId];
  if (!room || !room.lockedUntilFlag) return false;
  delete room.lockedUntilFlag;
  return true;
}

// ---------------------------------------------------------------
// DNS レコード（章 3）
// ---------------------------------------------------------------

/**
 * DNS レコードの追加・修正・削除。
 * address が null なら削除する（レコードを消して名前解決を落とす、も表現できる）。
 * ホスト名は大文字小文字と末尾のドットを無視して既存のキーに合わせる
 * （nslookup 側の照合と同じ規則にそろえる）。
 */
export function setDnsRecord(
  world: NetworkWorld,
  deviceId: DeviceId,
  hostname: string,
  address: string | null,
): boolean {
  const device = world.devices[deviceId];
  if (!device) return false;

  const normalize = (value: string): string => value.trim().toLowerCase().replace(/\.$/, '');
  const needle = normalize(hostname);
  if (needle.length === 0) return false;

  if (!device.dnsRecords) {
    if (address === null) return false;
    device.dnsRecords = {};
  }
  const existingKey = Object.keys(device.dnsRecords).find((key) => normalize(key) === needle);

  if (address === null) {
    if (existingKey === undefined) return false;
    delete device.dnsRecords[existingKey];
    return true;
  }

  const key = existingKey ?? hostname.trim();
  if (device.dnsRecords[key] === address) return false;
  device.dnsRecords[key] = address;
  return true;
}

// ---------------------------------------------------------------
// 無線（章 4）
// ---------------------------------------------------------------

/**
 * チャンネルの変更。
 * いまの周波数帯で使えないチャンネルは適用しない
 * （5GHz の AP に 6ch を入れる、のような成立しない状態を作らせない）。
 */
export function setWirelessChannel(
  world: NetworkWorld,
  deviceId: DeviceId,
  channel: number,
): boolean {
  const wireless = world.devices[deviceId]?.wireless;
  if (!wireless) return false;
  const band: WirelessBand = wireless.band === '5GHz' ? '5GHz' : '2.4GHz';
  if (!isValidChannel(band, channel)) {
    console.warn(`[sim] ${band} では ${channel}ch は使えません（${deviceId}）`);
    return false;
  }
  if (wireless.channel === channel) return false;
  wireless.channel = channel;
  return true;
}

/**
 * 周波数帯の変更。
 * 変更後の帯で今のチャンネルが使えない場合は、空いているチャンネルへ自動で寄せる
 * （5GHz に切り替えた瞬間だけ成立しない設定になる、という穴を作らない）。
 */
export function setWirelessBand(
  world: NetworkWorld,
  deviceId: DeviceId,
  band: WirelessBand,
): boolean {
  const wireless = world.devices[deviceId]?.wireless;
  if (!wireless) return false;
  const changed = wireless.band !== band;
  wireless.band = band;
  if (!isValidChannel(band, wireless.channel)) {
    wireless.channel = recommendChannel(world, deviceId);
  }
  return changed;
}

// ---------------------------------------------------------------
// 通信量・感染（章 6）
// ---------------------------------------------------------------

/**
 * 機器が出している通信量（Mbps）を変える。
 *
 * 負の値は 0 に丸める（「マイナスの通信」は存在しない）。
 * trafficMbps は省略可能な項目なので、未設定は 0 と同じものとして扱う。
 * **同じ値を何度入れても変化なしを返す**ので、章の setup を繰り返しても壊れない。
 */
export function setDeviceTraffic(
  world: NetworkWorld,
  deviceId: DeviceId,
  mbps: number,
): boolean {
  const device = world.devices[deviceId];
  if (!device) return false;
  const next = Number.isFinite(mbps) ? Math.max(0, Math.round(mbps * 10) / 10) : 0;
  if ((device.trafficMbps ?? 0) === next) return false;
  device.trafficMbps = next;
  return true;
}

/**
 * 感染フラグの上げ下げ。
 *
 * **通信量は変えない。** 「感染している」と「大量の通信を出している」は
 * 別のことで、章 6 はその 2 つを結び付ける前に、まず計測で異常を見つけさせる。
 * 両方を動かしたい場合は setDeviceTraffic と 2 つ並べて書く。
 */
export function setInfected(
  world: NetworkWorld,
  deviceId: DeviceId,
  infected: boolean,
): boolean {
  const device = world.devices[deviceId];
  if (!device || !!device.infected === infected) return false;
  device.infected = infected;
  return true;
}

// ---------------------------------------------------------------
// ファイアウォール（章 5）
// ---------------------------------------------------------------

/** patch で書き換えてよいフィールド。id / order / editable は含めない */
const FIREWALL_PATCH_KEYS = [
  'action',
  'srcSubnet',
  'dstSubnet',
  'dstPort',
  'protocol',
  'comment',
] as const;

/**
 * ファイアウォールルールの部分更新。
 *
 * **editable: false のルールには一切適用しない。**
 * 「触ってはいけない固定ルール」を UI からもシナリオからも壊せないようにする。
 * order は重複が出ないよう reorderFirewallRule() だけが変更できる。
 */
export function patchFirewallRule(
  world: NetworkWorld,
  deviceId: DeviceId,
  ruleId: string,
  patch: Record<string, unknown>,
): boolean {
  const rule = world.devices[deviceId]?.firewallRules?.find((r) => r.id === ruleId);
  if (!rule) return false;
  if (rule.editable === false) {
    console.warn(`[sim] ルール ${ruleId} は編集できません（editable: false）`);
    return false;
  }

  let changed = false;
  for (const key of FIREWALL_PATCH_KEYS) {
    if (!(key in patch)) continue;
    const value = patch[key];
    switch (key) {
      case 'action':
        if ((value === 'allow' || value === 'deny') && rule.action !== value) {
          rule.action = value;
          changed = true;
        }
        break;
      case 'srcSubnet':
      case 'dstSubnet':
        if (typeof value === 'string' && rule[key] !== value) {
          rule[key] = value;
          changed = true;
        }
        break;
      case 'protocol':
        if ((value === 'tcp' || value === 'udp' || value === '*') && rule.protocol !== value) {
          rule.protocol = value;
          changed = true;
        }
        break;
      case 'dstPort': {
        const next =
          value === '*' ? '*' : typeof value === 'number' && Number.isInteger(value) ? value : null;
        if (next !== null && rule.dstPort !== next) {
          rule.dstPort = next;
          changed = true;
        }
        break;
      }
      case 'comment':
        if (typeof value === 'string' && rule.comment !== value) {
          rule.comment = value;
          changed = true;
        }
        break;
    }
  }
  return changed;
}

/**
 * ルールの並べ替え。
 *
 * 指定したルールを「order の値が targetOrder になる位置」へ移し、
 * **他のルールの order も詰め直して重複が出ないようにする。**
 *
 * 元の order の値（10/20/30 のような間隔）は「枠」として保存し、
 * 並べ替え後の順番に沿って割り当て直す。
 * 元データに重複があった場合だけ、最小値から 1 ずつの連番に振り直して修復する。
 *
 * 【固定ルール（editable: false）の扱い】
 * 章5 が教えるのは「並び順そのものが設定の一部」であること。
 * だから固定ルールは、**中身だけでなく位置も**守らなければならない。
 * 位置が動かせると、外からの侵入を止める固定ルールを
 * 広い許可ルールの下へ落とせてしまい、管理画面の説明文とも食い違う。
 *
 * そこで規則を 2 つ置く:
 *   1. 固定ルール自身は動かせない（false を返して何もしない）
 *   2. 固定ルールが占める「枠」は動かさず、**可動ルールだけを残りの枠に詰め直す**
 * 2 のおかげで、他の行をどう動かしても固定ルールの order と行位置は変わらない。
 */
export function reorderFirewallRule(
  world: NetworkWorld,
  deviceId: DeviceId,
  ruleId: string,
  targetOrder: number,
): boolean {
  const rules = world.devices[deviceId]?.firewallRules;
  if (!rules || rules.length === 0) return false;

  const sorted = rules
    .map((rule, index) => ({ rule, index }))
    .sort((a, b) => a.rule.order - b.rule.order || a.index - b.index)
    .map((entry) => entry.rule);

  const target = sorted.find((rule) => rule.id === ruleId);
  if (!target) return false;
  if (target.editable === false) {
    console.warn(`[sim] ルール ${ruleId} は順序を変えられません（editable: false）`);
    return false;
  }

  // 割り当て直す order の「枠」
  const values = sorted.map((rule) => rule.order);
  const slots =
    new Set(values).size === values.length
      ? values
      : values.map((_, i) => Math.min(...values) + i);

  // 固定ルールの枠は据え置き。可動ルールだけを、その残りの枠に並べ直す
  const movableAt: number[] = [];
  sorted.forEach((rule, index) => {
    if (rule.editable !== false) movableAt.push(index);
  });
  const movable = movableAt.map((index) => sorted[index]);
  const movableSlots = movableAt.map((index) => slots[index]);

  const from = movable.findIndex((rule) => rule.id === ruleId);
  const to = Math.min(
    movable.length - 1,
    Math.max(0, movableSlots.filter((value) => value < targetOrder).length),
  );

  if (to === from) {
    // 位置が変わらなくても、重複していた order の修復だけは行う
    return applySlots(sorted, slots);
  }

  const moved = movable.splice(from, 1)[0];
  movable.splice(to, 0, moved);
  movableAt.forEach((index, k) => {
    sorted[index] = movable[k];
  });
  applySlots(sorted, slots);
  return true;
}

function applySlots(sorted: FirewallRule[], slots: number[]): boolean {
  let changed = false;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].order !== slots[i]) {
      sorted[i].order = slots[i];
      changed = true;
    }
  }
  return changed;
}
